import { utcStringToDate } from './utilities';

const UNPAID_STATUS_WORDS = ['billed', 'failed', 'pending'];
const REVIEW_STATUS_WORDS = ['review'];
const FINAL_STATUS_WORDS = ['completed', 'closed', 'cancelled', 'canceled', 'refunded', 'rejected', 'denied'];
const REFILL_ELIGIBILITY_OFFSET_DAYS = -60;

export const isUnpaidOrder = (order) => {
  const status = String(order?.order_status || '').toLowerCase();
  return UNPAID_STATUS_WORDS.some(word => status.includes(word));
};

export const isUnderProviderReview = (order) => {
  const rxStatus = String(order?.rx_status || '').toLowerCase();
  const orderStatus = String(order?.order_status || '').toLowerCase();
  return REVIEW_STATUS_WORDS.some(word => rxStatus.includes(word) || orderStatus.includes(word));
};

export const isOrderInFlight = (order) => {
  if (!order) return false;
  const status = String(order.order_status || '').toLowerCase();
  if (!status) return true;
  return !FINAL_STATUS_WORDS.some(word => status.includes(word));
};

export const getRefillBaseDate = (orderOrDate) => {
  const value = orderOrDate?.next_refill ?? orderOrDate;
  if (!value) return null;
  if (!(value instanceof Date) && typeof value !== 'string') return null;
  const date = value instanceof Date ? new Date(value) : utcStringToDate(value);
  return date && !Number.isNaN(date.getTime()) ? date : null;
};

export const getRefillEligibleDate = (orderOrDate) => {
  const baseDate = getRefillBaseDate(orderOrDate);
  if (!baseDate) return null;

  const eligibleDate = new Date(baseDate);
  eligibleDate.setDate(baseDate.getDate() + REFILL_ELIGIBILITY_OFFSET_DAYS);
  return eligibleDate;
};

export const getDaysUntilRefill = (orderOrDate) => {
  const eligibleDate = getRefillEligibleDate(orderOrDate);
  if (!eligibleDate) return 0;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  eligibleDate.setHours(0, 0, 0, 0);
  return Math.ceil((eligibleDate - today) / (1000 * 60 * 60 * 24));
};

// The most recent order for the SAME product as `order`. `orders` is expected
// pre-sorted newest-first (OrderContext.orders already is), so the first
// matching row is that product's latest.
export const getLatestOrderForProduct = (order, orders = []) => {
  if (!order || !Array.isArray(orders)) return null;
  return orders.find(o => o?.product_name === order.product_name) || null;
};

export const getRefillActionState = (order, orders) => {
  const latestForProduct = getLatestOrderForProduct(order, orders);
  const isLatestOrder = Boolean(latestForProduct && order?.order_id === latestForProduct.order_id);
  const isPaid = !isUnpaidOrder(order);
  const isReview = isUnderProviderReview(order);
  const daysUntilRefill = getDaysUntilRefill(order);
  const isRefillable = isLatestOrder && isPaid && !isReview && daysUntilRefill <= 0;
  const showRefillCountdown = isLatestOrder && isPaid && !isReview && daysUntilRefill > 0;

  return {
    isLatestOrder,
    isPaid,
    isUnpaid: !isPaid,
    isUnderReview: isReview,
    daysUntilRefill,
    isRefillable,
    showRefillCountdown,
  };
};

export const getFirstUnpaidOrder = (orders = []) => {
  if (!Array.isArray(orders)) return null;
  return orders.find(isUnpaidOrder) || null;
};
